import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Badge, Row, Col, Spinner, Alert } from "reactstrap";
import { Table } from "antd";
import dayjs from "dayjs";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../components/Component";
import customerAdminApis from "../../api/customer-admin/customerAdmin";
import { formatCurrency } from "../../utils/Utils";

const SubscriptionAccess = () => {
  const { sm, updateSm } = useOutletContext<any>();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [accessCheck, setAccessCheck] = useState<any>(null);
  const [billingConfig, setBillingConfig] = useState<any>(null);

  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && currentUser.genericRole !== "admin") {
      navigate(`${process.env.PUBLIC_URL}/dashboard`);
      return;
    }
    fetchData();
  }, [currentUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, accessRes, billingRes] = await Promise.all([
        customerAdminApis.fetchSubscriptionOverview(),
        customerAdminApis.fetchAccessCheck(),
        customerAdminApis.fetchBillingConfig(),
      ]);
      if (subRes?.status) setSubscription(subRes.data);
      if (accessRes?.status) setAccessCheck(accessRes.data);
      if (billingRes?.status) setBillingConfig(billingRes.data);
    } catch (error) {
      console.error("Failed to fetch customer admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const moduleColumns = [
    {
      title: "Module Name",
      dataIndex: "moduleName",
      key: "moduleName",
    },
    {
      title: "Valid From",
      dataIndex: "validFrom",
      key: "validFrom",
      render: (date: string) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Valid To",
      dataIndex: "validTo",
      key: "validTo",
      render: (date: string) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Badge color={isActive ? "success" : "danger"}>
          {isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <React.Fragment>
      <Head title="Subscription & Access" />
      <BlockHead size="lg">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle tag="h4">Subscription & Access</BlockTitle>
            <BlockDes>
              <p>View your subscription details, access status, and billing configuration.</p>
            </BlockDes>
          </BlockHeadContent>
          <BlockHeadContent className="align-self-start d-lg-none">
            <Button
              className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""}`}
              onClick={() => updateSm(!sm)}
            >
              <Icon name="menu-alt-r" />
            </Button>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      {/* Access Status */}
      {accessCheck && (
        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <h6 className="overline-title-alt mb-3">Access Status</h6>
              <Row className="gy-3">
                <Col sm="4">
                  <span className="sub-text">Status</span>
                  <span className="caption-text">
                    <Badge
                      color={accessCheck.isRestricted ? "danger" : "success"}
                      className="ms-0"
                    >
                      {accessCheck.isRestricted ? "Restricted" : "Active"}
                    </Badge>
                  </span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Billing Status</span>
                  <span className="caption-text">
                    <Badge
                      color={
                        accessCheck.billingStatus === "Active"
                          ? "success"
                          : accessCheck.billingStatus === "Hold"
                          ? "danger"
                          : "warning"
                      }
                    >
                      {accessCheck.billingStatus}
                    </Badge>
                  </span>
                </Col>
                {accessCheck.isRestricted && (
                  <Col sm="12">
                    <Alert color="warning" className="mb-0">
                      <strong>{accessCheck.restrictionReason === "non-payment" ? "Payment Due" : "Setup Pending"}:</strong>{" "}
                      {accessCheck.message}
                    </Alert>
                  </Col>
                )}
              </Row>
            </div>
          </Card>
        </Block>
      )}

      {/* Billing Configuration */}
      {billingConfig && (
        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <h6 className="overline-title-alt mb-3">Billing Configuration</h6>
              <Row className="gy-3">
                <Col sm="4">
                  <span className="sub-text">Billing Mode</span>
                  <span className="caption-text">{billingConfig.billingMode || "-"}</span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Billing Frequency</span>
                  <span className="caption-text">{billingConfig.billingFrequency || "-"}</span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Payment Due Days</span>
                  <span className="caption-text">{billingConfig.paymentDueDays ?? "-"} days</span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Billing Start Date</span>
                  <span className="caption-text">
                    {billingConfig.billingStartDate
                      ? dayjs(billingConfig.billingStartDate).format("DD-MMM-YYYY")
                      : "-"}
                  </span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Billing End Date</span>
                  <span className="caption-text">
                    {billingConfig.billingEndDate
                      ? dayjs(billingConfig.billingEndDate).format("DD-MMM-YYYY")
                      : "-"}
                  </span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Billing Type</span>
                  <span className="caption-text">{billingConfig.billingType || "-"}</span>
                </Col>
              </Row>
              {(billingConfig.maxUsers || billingConfig.maxProjects || billingConfig.maxSuppliers) && (
                <>
                  <hr className="my-3" />
                  <h6 className="overline-title-alt mb-3">Usage Limits</h6>
                  <Row className="gy-3">
                    {billingConfig.maxUsers != null && (
                      <Col sm="4">
                        <span className="sub-text">Max Users</span>
                        <span className="caption-text">{billingConfig.maxUsers}</span>
                      </Col>
                    )}
                    {billingConfig.maxProjects != null && (
                      <Col sm="4">
                        <span className="sub-text">Max Projects</span>
                        <span className="caption-text">{billingConfig.maxProjects}</span>
                      </Col>
                    )}
                    {billingConfig.maxSuppliers != null && (
                      <Col sm="4">
                        <span className="sub-text">Max Suppliers</span>
                        <span className="caption-text">{billingConfig.maxSuppliers}</span>
                      </Col>
                    )}
                  </Row>
                </>
              )}
            </div>
          </Card>
        </Block>
      )}

      {/* Advance Balance Summary */}
      {subscription?.advanceSummary && (
        <Block>
          <h6 className="overline-title-alt mb-3">Advance Balance</h6>
          <Row className="gy-3">
            <Col md="4">
              <Card className="card-bordered text-center p-3">
                <span className="sub-text">Total Advance Paid</span>
                <span className="lead-text text-primary">
                  {formatCurrency(subscription.advanceSummary.totalAdvancePaid, "INR")}
                </span>
              </Card>
            </Col>
            <Col md="4">
              <Card className="card-bordered text-center p-3">
                <span className="sub-text">Total Used</span>
                <span className="lead-text text-warning">
                  {formatCurrency(subscription.advanceSummary.totalUsed, "INR")}
                </span>
              </Card>
            </Col>
            <Col md="4">
              <Card className="card-bordered text-center p-3">
                <span className="sub-text">Available Balance</span>
                <span className="lead-text text-success">
                  {formatCurrency(subscription.advanceSummary.availableBalance, "INR")}
                </span>
              </Card>
            </Col>
          </Row>
        </Block>
      )}

      {/* Enabled Modules */}
      {subscription?.enabledModules && subscription.enabledModules.length > 0 && (
        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <h6 className="overline-title-alt mb-3">Enabled Modules</h6>
              <Table
                columns={moduleColumns}
                dataSource={subscription.enabledModules}
                rowKey="customerModulePriceId"
                pagination={false}
                size="small"
              />
            </div>
          </Card>
        </Block>
      )}
    </React.Fragment>
  );
};

export default SubscriptionAccess;
